/**
 * The consumption certificate's two required uploads (exterior view, heater nameplate), recorded in the
 * configuration through `useGatedUploadScope`. Completeness reads the recorded filenames, never the live
 * upload areas.
 */

import type { CartItemId } from 'models/purchaseFlowRevamp'

import { usePfStore } from '../../store'
import {
  useExteriorViewUpload,
  useGatedUploadScope,
  useHeaterNameplateUpload,
  useLineDocScope,
  useSessionDocumentsDropped
} from '../../fileHub'
import { useConsumptionCertificateConfig } from './useConsumptionCertificateConfig'

export function useConsumptionUploadController(cartItemId: CartItemId) {
  const { value, patch } = useConsumptionCertificateConfig(cartItemId)

  const variantId = usePfStore((s) => s.items.find((i) => i.id === cartItemId)?.variantId ?? null)
  const exterior = useLineDocScope(useExteriorViewUpload(), cartItemId, variantId)
  const nameplate = useLineDocScope(useHeaterNameplateUpload(), cartItemId, variantId)

  const dropped = useSessionDocumentsDropped(cartItemId, 'consumption_certificate')

  const exteriorGate = useGatedUploadScope(exterior, exterior.filenames, value.exteriorViewFilenames,
    (filenames) => patch({ exteriorViewFilenames: filenames }))
  const nameplateGate = useGatedUploadScope(nameplate, nameplate.filenames, value.heaterNameplateFilenames,
    (filenames) => patch({ heaterNameplateFilenames: filenames }))

  return {
    exterior,
    nameplate,
    hasRestoredFiles: dropped && !(exteriorGate.touched || nameplateGate.touched),
    uploadExterior: exteriorGate.upload,
    deleteExterior: exteriorGate.remove,
    uploadNameplate: nameplateGate.upload,
    deleteNameplate: nameplateGate.remove,
  }
}
